<!-- 商家端 - 营销管理 -->
<template>
  <ContentWrap>
    <el-tabs v-model="activeTab" @tab-change="onTabChange">
      <el-tab-pane label="优惠券" name="coupon" />
      <el-tab-pane label="限时折扣" name="discount" />
      <el-tab-pane label="秒杀" name="seckill" />
      <el-tab-pane label="满减送" name="reward" />
      <el-tab-pane label="砍价" name="bargain" />
      <el-tab-pane label="拼团" name="combination" />
    </el-tabs>

    <!-- 搜索 + 新增 -->
    <el-form :inline="true" :model="queryParams" class="mt-10px">
      <el-form-item :label="nameFieldLabel">
        <el-input v-model="queryParams.name" class="!w-200px" clearable placeholder="请输入名称" @keyup.enter="getList" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" class="!w-140px" clearable placeholder="全部">
          <el-option label="开启" :value="0" />
          <el-option label="关闭" :value="1" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="getList"><Icon class="mr-5px" icon="ep:search" />搜索</el-button>
        <el-button plain type="primary" @click="openCreate"><Icon class="mr-5px" icon="ep:plus" />新增</el-button>
        <el-button v-if="recordEntry" plain @click="openRecord">{{ recordEntry.label }}</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <!-- == 通用列 == -->
      <el-table-column :label="nameFieldLabel" min-width="160" prop="name" show-overflow-tooltip />
      <el-table-column label="类型" width="100">
        <template #default>{{ tabLabel(activeTab) }}</template>
      </el-table-column>

      <!-- == 优惠券专属列 == -->
      <el-table-column v-if="activeTab === 'coupon'" label="发放数量" width="90" prop="totalCount" align="center" />
      <el-table-column v-if="activeTab === 'coupon'" label="剩余" width="80" align="center">
        <template #default="{ row }">{{ (row.totalCount ?? 0) - (row.takeCount ?? 0) }}</template>
      </el-table-column>
      <el-table-column v-if="activeTab === 'coupon'" label="领取上限" width="90" prop="takeLimitCount" align="center" />

      <!-- == 限时折扣 / 满减送 / 秒杀 / 砍价 / 拼团 专属列 == -->
      <el-table-column v-if="activeTab !== 'coupon'" label="活动时间" width="240">
        <template #default="{ row }">
          {{ formatTime(row.startTime) }} ~ {{ formatTime(row.endTime) }}
        </template>
      </el-table-column>

      <!-- == 秒杀 / 砍价 / 拼团 专属列 == -->
      <el-table-column v-if="seckillBargainCombo.includes(activeTab)" label="商品" min-width="200" prop="spuName" show-overflow-tooltip />

      <!-- == 秒杀 专属列 == -->
      <el-table-column v-if="activeTab === 'seckill'" label="秒杀价" width="100" align="center">
        <template #default="{ row }">{{ fenToYuan(row.seckillPrice) }} 元</template>
      </el-table-column>
      <el-table-column v-if="activeTab === 'seckill'" label="库存" width="80" prop="stock" align="center" />

      <!-- == 砍价 专属列 == -->
      <el-table-column v-if="activeTab === 'bargain'" label="砍价底价" width="100" align="center">
        <template #default="{ row }">{{ fenToYuan(row.bargainMinPrice) }} 元</template>
      </el-table-column>
      <el-table-column v-if="activeTab === 'bargain'" label="已砍人数" width="90" prop="recordUserCount" align="center" />
      <el-table-column v-if="activeTab === 'bargain'" label="库存" width="80" prop="stock" align="center" />

      <!-- == 拼团 专属列 == -->
      <el-table-column v-if="activeTab === 'combination'" label="拼团价" width="100" align="center">
        <template #default="{ row }">{{ fenToYuan(row.combinationPrice) }} 元</template>
      </el-table-column>
      <el-table-column v-if="activeTab === 'combination'" label="开团组数" width="90" prop="groupCount" align="center" />
      <el-table-column v-if="activeTab === 'combination'" label="成团组数" width="90" prop="groupSuccessCount" align="center" />

      <!-- == 状态：开关（0 开启 / 1 关闭），各 Tab 共用 == -->
      <el-table-column label="状态" width="100" align="center">
        <template #default="{ row }">
          <el-switch
            v-model="row.status"
            :active-value="0"
            :inactive-value="1"
            inline-prompt
            active-text="开"
            inactive-text="关"
            :disabled="isStatusSwitchDisabled(row)"
            @change="(val) => handleStatusChange(row, val)"
          />
        </template>
      </el-table-column>
      <el-table-column label="创建时间" width="170" prop="createTime" :formatter="dateFormatter" />
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openEdit(row.id)">编辑</el-button>
          <el-button link type="danger" @click="handleDelete(row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination v-model:limit="queryParams.pageSize" v-model:page="queryParams.pageNo" :total="total" @pagination="getList" />
  </ContentWrap>

  <!-- 表单：复用管理后台已有的 promotion form 组件，表单自带弹窗 -->
  <component
    v-if="formVisible"
    ref="formComponentRef"
    :is="formComponent"
    :id="formId"
    merchant
    @success="onFormSuccess"
  />
</template>

<script setup lang="ts">
import * as MerchantPromotionApi from '@/api/merchant/promotion'
import { markRaw, nextTick } from 'vue'
import { dateFormatter } from '@/utils/formatTime'
import { fenToYuan } from '@/utils'
import { CommonStatusEnum } from '@/utils/constants'

defineOptions({ name: 'MerchantPromotion' })

const message = useMessage()
const router = useRouter()

const activeTab = ref('coupon')
const loading = ref(false)
const list = ref([])
const total = ref(0)
const queryParams = reactive({ pageNo: 1, pageSize: 10, name: '', status: undefined })

/** 优惠券 Tab 用「优惠券名称」，其它活动仍用「活动名称」 */
const nameFieldLabel = computed(() => (activeTab.value === 'coupon' ? '优惠券名称' : '活动名称'))

// 表单
const formVisible = ref(false)
const formId = ref<number | undefined>(undefined)
const formComponent = ref<any>(null)
const formComponentRef = ref<any>(null)

const tabMap: Record<string, string> = { coupon: '优惠券', discount: '限时折扣', seckill: '秒杀', reward: '满减送', bargain: '砍价', combination: '拼团' }
const tabLabel = (t: string) => tabMap[t] || t

// 需要显示商品列的 tab
const seckillBargainCombo = ['seckill', 'bargain', 'combination']

// 时间戳格式化（用于 template 中非 el-table-column 的场景）
const formatTime = (ts: any) => {
  if (!ts) return '-'
  const d = new Date(ts)
  if (isNaN(d.getTime())) return String(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

// 按 Tab 动态选择 API
const apiMap: Record<string, any> = {
  coupon:       { page: MerchantPromotionApi.getCouponPage, close: MerchantPromotionApi.updateCouponStatus, del: MerchantPromotionApi.deleteCoupon },
  discount:     { page: MerchantPromotionApi.getDiscountPage, close: MerchantPromotionApi.closeDiscount, del: MerchantPromotionApi.deleteDiscount },
  seckill:      {
    page: MerchantPromotionApi.getSeckillPage,
    close: MerchantPromotionApi.closeSeckill,
    open: MerchantPromotionApi.openSeckill,
    del: MerchantPromotionApi.deleteSeckill
  },
  reward:       { page: MerchantPromotionApi.getRewardPage, close: MerchantPromotionApi.closeReward, del: MerchantPromotionApi.deleteReward },
  bargain:      { page: MerchantPromotionApi.getBargainPage, close: MerchantPromotionApi.closeBargain, del: MerchantPromotionApi.deleteBargain },
  combination:  { page: MerchantPromotionApi.getCombinationPage, close: MerchantPromotionApi.closeCombination, del: MerchantPromotionApi.deleteCombination },
}

const currentApi = computed(() => apiMap[activeTab.value])

const recordRouteMap: Record<string, { name: string; label: string }> = {
  coupon: { name: 'MerchantCouponRecord', label: '领取记录' },
  bargain: { name: 'MerchantBargainRecord', label: '砍价记录' },
  combination: { name: 'MerchantCombinationRecord', label: '拼团记录' }
}
const recordEntry = computed(() => recordRouteMap[activeTab.value])
const openRecord = () => {
  const entry = recordEntry.value
  if (entry) router.push({ name: entry.name })
}

const getList = async () => {
  loading.value = true
  try {
    const data = await currentApi.value.page(queryParams)
    // 统一成数字，避免接口返回字符串导致开关绑不上、误显示异常态
    list.value = (data.list || []).map((row: any) => ({
      ...row,
      status: row.status == null || row.status === '' ? CommonStatusEnum.ENABLE : Number(row.status)
    }))
    total.value = data.total
  } finally { loading.value = false }
}

const onTabChange = () => {
  queryParams.pageNo = 1
  getList()
}

const openCreate = () => {
  formId.value = undefined
  formVisible.value = true
  loadFormComponent('create')
}

const openEdit = async (id: number) => {
  formId.value = id
  formVisible.value = true
  loadFormComponent('update')
}

const loadFormComponent = async (type: string) => {
  const tab = activeTab.value
  const compMap: Record<string, () => Promise<any>> = {
    coupon:      () => import('@/views/mall/promotion/coupon/template/CouponTemplateForm.vue'),
    discount:    () => import('@/views/mall/promotion/discountActivity/DiscountActivityForm.vue'),
    seckill:     () => import('@/views/mall/promotion/seckill/activity/SeckillActivityForm.vue'),
    reward:      () => import('@/views/mall/promotion/rewardActivity/RewardForm.vue'),
    bargain: () => import('@/views/mall/promotion/bargain/activity/BargainActivityForm.vue'),
    combination: () => import('@/views/mall/promotion/combination/activity/CombinationActivityForm.vue'),
  }
  const loader = compMap[tab]
  if (loader) {
    const mod = await loader()
    formComponent.value = markRaw(mod.default)
    // 等待组件挂载后，调用其 open 方法打开表单弹窗
    await nextTick()
    formComponentRef.value?.open(type, formId.value)
  }
}

const onFormSuccess = () => {
  formVisible.value = false
  getList()
}

/** 优惠券 / 秒杀可反复开关；其余活动 close 后不可再开 */
const isStatusSwitchDisabled = (row: any) =>
  !['coupon', 'seckill'].includes(activeTab.value) &&
  Number(row.status) === CommonStatusEnum.DISABLE

/** 状态开关变更 */
const handleStatusChange = async (row: any, nextStatus: number) => {
  const enabling = Number(nextStatus) === CommonStatusEnum.ENABLE
  const text = enabling ? '开启' : '关闭'
  const prevStatus = enabling ? CommonStatusEnum.DISABLE : CommonStatusEnum.ENABLE
  try {
    await message.confirm(`确认${text}「${row.name}」？`)
    if (activeTab.value === 'coupon') {
      await MerchantPromotionApi.updateCouponStatus({ id: row.id, status: Number(nextStatus) })
    } else if (enabling) {
      if (typeof currentApi.value.open !== 'function') {
        message.warning('该活动关闭后不可重新开启')
        row.status = prevStatus
        return
      }
      await currentApi.value.open(row.id)
    } else {
      await currentApi.value.close(row.id)
    }
    row.status = Number(nextStatus)
    message.success(`${text}成功`)
  } catch {
    row.status = prevStatus
  }
}

const handleDelete = async (id: number) => {
  try {
    await message.confirm('确认删除该活动？删除后不可恢复！')
    await currentApi.value.del(id)
    message.success('已删除')
    getList()
  } catch {}
}

onMounted(() => { getList() })
</script>
